import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { FiPlus } from "react-icons/fi";

import { TYPE_META, STATUS_META, formatDate, formatDateRange, dayCount } from "../TimeOff/TimeOff";

// Inline icons from the design (16px, stroked in currentColor so the parent
// controls the tint). The rejection-reason icon is a 20px filled circle-x.
const strokeProps = {
  stroke: "currentColor",
  strokeWidth: 1.33333,
  strokeLinecap: "round",
  strokeLinejoin: "round",
};

function CalendarIcon({ size = 16, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className}>
      <path d="M5.33301 1.33325V3.99992" {...strokeProps} />
      <path d="M10.667 1.33325V3.99992" {...strokeProps} />
      <path
        d="M12.6667 2.66675H3.33333C2.59695 2.66675 2 3.2637 2 4.00008V13.3334C2 14.0698 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0698 14 13.3334V4.00008C14 3.2637 13.403 2.66675 12.6667 2.66675Z"
        {...strokeProps}
      />
      <path d="M2 6.66675H14" {...strokeProps} />
      <path d="M5.33301 9.33325H5.33967" {...strokeProps} />
      <path d="M8 9.33325H8.00667" {...strokeProps} />
      <path d="M10.667 9.33325H10.6737" {...strokeProps} />
      <path d="M5.33301 12H5.33967" {...strokeProps} />
      <path d="M8 12H8.00667" {...strokeProps} />
      <path d="M10.667 12H10.6737" {...strokeProps} />
    </svg>
  );
}

// "Submitted" / "Approved Date" use the simpler calendar (header rule, no dots).
function CalendarSimpleIcon({ size = 16, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className}>
      <path d="M5.33301 1.33325V3.99992" {...strokeProps} />
      <path d="M10.667 1.33325V3.99992" {...strokeProps} />
      <path
        d="M12.6667 2.66675H3.33333C2.59695 2.66675 2 3.2637 2 4.00008V13.3334C2 14.0698 2.59695 14.6667 3.33333 14.6667H12.6667C13.403 14.6667 14 14.0698 14 13.3334V4.00008C14 3.2637 13.403 2.66675 12.6667 2.66675Z"
        {...strokeProps}
      />
      <path d="M2 6.66675H14" {...strokeProps} />
    </svg>
  );
}

function ClockIcon({ size = 16, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className}>
      <path
        d="M7.99967 14.6666C11.6816 14.6666 14.6663 11.6818 14.6663 7.99992C14.6663 4.31802 11.6816 1.33325 7.99967 1.33325C4.31778 1.33325 1.33301 4.31802 1.33301 7.99992C1.33301 11.6818 4.31778 14.6666 7.99967 14.6666Z"
        {...strokeProps}
      />
      <path d="M8 4V8L10.6667 9.33333" {...strokeProps} />
    </svg>
  );
}

function NotesIcon({ size = 16, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className}>
      <path d="M10 8H2" {...strokeProps} />
      <path d="M11.3333 12H2" {...strokeProps} />
      <path d="M14 4H2" {...strokeProps} />
    </svg>
  );
}

function UserIcon({ size = 16, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" className={className}>
      <path
        d="M12.6663 14V12.6667C12.6663 11.9594 12.3854 11.2811 11.8853 10.781C11.3852 10.281 10.7069 10 9.99967 10H5.99967C5.29243 10 4.61415 10.281 4.11406 10.781C3.61396 11.2811 3.33301 11.9594 3.33301 12.6667V14"
        {...strokeProps}
      />
      <path
        d="M7.99967 7.33333C9.47243 7.33333 10.6663 6.13943 10.6663 4.66667C10.6663 3.19391 9.47243 2 7.99967 2C6.52691 2 5.33301 3.19391 5.33301 4.66667C5.33301 6.13943 6.52691 7.33333 7.99967 7.33333Z"
        {...strokeProps}
      />
    </svg>
  );
}

function RejectIcon({ size = 20, className }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}>
      <path
        d="M17.7087 10.0001C17.7087 5.74289 14.2575 2.29175 10.0003 2.29175C5.74313 2.29175 2.29199 5.74289 2.29199 10.0001C2.29199 14.2573 5.74313 17.7084 10.0003 17.7084C14.2575 17.7084 17.7087 14.2573 17.7087 10.0001ZM18.9587 10.0001C18.9587 14.9476 14.9479 18.9584 10.0003 18.9584C5.05277 18.9584 1.04199 14.9476 1.04199 10.0001C1.04199 5.05253 5.05277 1.04175 10.0003 1.04175C14.9479 1.04175 18.9587 5.05253 18.9587 10.0001Z"
        fill="currentColor"
      />
      <path
        d="M12.0584 7.05819C12.3025 6.81411 12.6981 6.81411 12.9422 7.05819C13.1863 7.30226 13.1863 7.6979 12.9422 7.94198L7.94222 12.942C7.69814 13.1861 7.30251 13.1861 7.05843 12.942C6.81435 12.6979 6.81435 12.3023 7.05843 12.0582L12.0584 7.05819Z"
        fill="currentColor"
      />
      <path
        d="M7.05843 7.05819C7.30251 6.81411 7.69814 6.81411 7.94222 7.05819L12.9422 12.0582C13.1863 12.3023 13.1863 12.6979 12.9422 12.942C12.6981 13.1861 12.3025 13.1861 12.0584 12.942L7.05843 7.94198C6.81435 7.6979 6.81435 7.30226 7.05843 7.05819Z"
        fill="currentColor"
      />
    </svg>
  );
}

// Right-side drawer showing a requester's own time-off request (read-only).
// Mirrors TimeOffDetail's mount/slide animation, but the body and footer are
// driven by the request's status rather than admin approve/reject controls.
//
// The parent owns `item` (the request to show, or null) and clears it on close.
function MyTimeOffDetail({ item, onClose, onCreateNew }) {
  const [open, setOpen] = useState(false);
  const [displayed, setDisplayed] = useState(null);
  const drawerRef = useRef(null);
  const pendingOpen = useRef(false);

  useEffect(() => {
    if (item) {
      setDisplayed(item);
      pendingOpen.current = true;
      setOpen(false);
      return;
    }
    setOpen(false);
  }, [item]);

  useLayoutEffect(() => {
    if (!pendingOpen.current || !displayed) return;
    pendingOpen.current = false;
    // eslint-disable-next-line no-unused-expressions
    drawerRef.current?.offsetWidth; // force reflow at the closed position
    setOpen(true);
  }, [displayed]);

  const handleTransitionEnd = (e) => {
    if (e.target !== e.currentTarget) return;
    if (!open) {
      setDisplayed(null);
      onClose?.();
    }
  };

  if (!displayed) return null;

  const type = TYPE_META[displayed.type] || TYPE_META[3];
  const TypeIcon = type.icon;
  const status = STATUS_META[displayed.status] || STATUS_META[0];
  const days = dayCount(displayed.startDate, displayed.endDate);

  const isApproved = displayed.status === 1 || displayed.status === 2;
  const isRejected = displayed.status === 3;
  const isCancelled = displayed.status === 10;

  const decidedDate = displayed.decidedAt ? formatDate(displayed.decidedAt) : "-";
  const approverName = displayed.approvedBy?.name || displayed.approvedBy?.email || "-";
  const rejecterName = displayed.rejectedBy?.name || displayed.rejectedBy?.email || "-";

  return (
    <>
      {/* backdrop */}
      <div
        className={`fixed inset-0 bg-black/20 z-40 transition-opacity duration-300 ease-out ${
          open ? "opacity-100" : "opacity-0"
        }`}
        onClick={() => setOpen(false)}
      />
      {/* drawer */}
      <div
        ref={drawerRef}
        onTransitionEnd={handleTransitionEnd}
        className={`fixed top-0 right-0 h-full w-[384px] max-w-[480px] flex flex-col border-l border-[#E5E8F3] bg-white shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] z-50 transition-transform duration-300 ease-out ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-4 p-6 border-b border-[#E5E8F3]">
          <div className="flex flex-1 items-center gap-4">
            <div className={`flex items-center justify-center w-10 h-10 rounded-lg ${type.tileClass}`}>
              {TypeIcon && <TypeIcon size={24} className={type.iconClass} />}
            </div>
            <div className="flex flex-1 flex-col gap-0.5">
              <span className="text-[18px] font-OpenSansSemiBold text-[#1A2332] capitalize leading-7">
                {type.label}
              </span>
              <span
                className={`inline-flex w-fit items-center px-2 py-0.5 rounded-2xl text-[12px] font-OpenSansSemiBold capitalize leading-[18px] tracking-[0.12px] ${status.className}`}
              >
                {status.label}
              </span>
            </div>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="flex items-center justify-center w-8 h-8 rounded-full text-[#868DA6] hover:text-[#1A2332] text-[20px] leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body (scrolls) */}
        <div className="flex-1 overflow-y-auto p-6 flex flex-col gap-6">
          {/* Request Information */}
          <div className="flex flex-col gap-3">
            <h4 className="text-[12px] font-OpenSansSemiBold text-[#7C829A] uppercase leading-[18px] tracking-[0.12px]">
              Request Information
            </h4>
            <div className="flex flex-col gap-4">
              <InfoRow
                icon={CalendarIcon}
                label="Dates"
                value={formatDateRange(displayed.startDate, displayed.endDate)}
              />
              <InfoRow icon={ClockIcon} label="Duration" value={`${days} ${days === 1 ? "day" : "days"}`} />
              <InfoRow icon={CalendarSimpleIcon} label="Submitted" value={formatDate(displayed.createdAt)} />
              <InfoRow icon={NotesIcon} label="Notes" value={displayed.notes || "—"} />
            </div>
          </div>

          {/* Approval Information */}
          {isApproved && (
            <>
              <div className="h-px bg-[#E5E8F3]" />
              <div className="flex flex-col gap-3">
                <h4 className="text-[12px] font-OpenSansSemiBold text-[#7C829A] uppercase leading-[18px] tracking-[0.12px]">
                  Approval Information
                </h4>
                <div className="flex flex-col gap-4">
                  <InfoRow icon={UserIcon} label="Approved By" value={approverName} />
                  <InfoRow icon={CalendarSimpleIcon} label="Approved Date" value={decidedDate} />
                </div>
              </div>
            </>
          )}

          {/* Rejection Information */}
          {isRejected && (
            <>
              <div className="h-px bg-[#E5E8F3]" />
              <div className="flex flex-col gap-3">
                <h4 className="text-[12px] font-OpenSansSemiBold text-[#7C829A] uppercase leading-[18px] tracking-[0.12px]">
                  Rejection Information
                </h4>
                <div className="flex flex-col gap-4">
                  <InfoRow icon={UserIcon} label="Rejected By" value={rejecterName} />
                  <InfoRow icon={CalendarSimpleIcon} label="Rejected Date" value={decidedDate} />
                </div>
                {displayed.rejectionReason && (
                  <div className="flex items-start gap-2.5 rounded-lg border border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
                    <RejectIcon size={20} className="mt-0.5 shrink-0 text-[#EF4444]" />
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[14px] font-OpenSansSemiBold text-[#1A2332] leading-5">
                        Rejection Reason
                      </span>
                      <span className="text-[14px] font-OpenSansRegular text-[#7C829A] leading-5 tracking-[0.14px]">
                        {displayed.rejectionReason}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* Actions notice (approved / cancelled) */}
          {(isApproved || isCancelled) && (
            <>
              <div className="h-px bg-[#E5E8F3]" />
              <div className="flex flex-col gap-3">
                <h4 className="text-[12px] font-OpenSansSemiBold text-[#7C829A] uppercase leading-[18px] tracking-[0.12px]">
                  Actions
                </h4>
                <p className="text-[14px] font-OpenSansRegular italic text-[#7C829A] leading-5">
                  {isApproved
                    ? "Approved requests cannot be edited. Contact your manager to make changes."
                    : "This request has been cancelled."}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Footer (status-specific actions) */}
        <div className="border-t border-[#E5E8F3] bg-white p-6 flex flex-col gap-3">
          {isRejected && (
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                onCreateNew?.();
              }}
              className="flex w-full items-center justify-center gap-2 h-12 px-6 rounded-lg bg-[#0065FD] hover:bg-[#0058DB] text-[16px] font-OpenSansSemiBold capitalize text-white leading-6"
            >
              <FiPlus size={18} />
              Create New Request
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="w-full h-12 px-6 rounded-lg border border-[#C9CEE2] bg-white shadow-[0_1px_2px_rgba(16,24,40,0.05)] text-[16px] font-OpenSansSemiBold capitalize text-[#1A2332] leading-6 hover:bg-[#F8FAFC]"
          >
            Close Details
          </button>
        </div>
      </div>
    </>
  );
}

// One labelled field with a leading icon, used in the info sections.
function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex min-w-[200px] items-start gap-3">
      <Icon size={16} className="mt-0.5 shrink-0 text-[#868DA6]" />
      <div className="flex flex-1 flex-col gap-0.5">
        <span className="text-[12px] font-OpenSansSemiBold text-[#7C829A] capitalize leading-[18px] tracking-[0.12px]">
          {label}
        </span>
        <span className="text-[14px] font-OpenSansRegular text-[#1A2332] leading-5 tracking-[0.14px] whitespace-pre-wrap">
          {value}
        </span>
      </div>
    </div>
  );
}

export default MyTimeOffDetail;
